import React, { useState, useMemo } from 'react'
import './staff-operate-record-table.scss'
import { useRequest } from 'ahooks'
import { IPagination } from '@/model/types/common'
import { $post } from '@/api'
import $msg from '@/utils/message'
import { IUser } from '@/model/types/user'
import { ColumnsType } from 'antd/lib/table'
import { getBEMCls } from '@/utils/classname'
import { Row } from '@/components/layout/layout'
import dayjs from 'dayjs'
import { OperateMap, getOperateDetail } from './staff-operate-record-type'
import { AutoResizer, WithScrollLoad } from '@/utils/utils-widget'
import { Table } from 'antd'
import { shopStore } from '@/store/shop'
import { StaffList } from '../../staff-list/staff-list-table/staff-list-table'
import { showLayer } from '@/utils/layer'
import VersionTip from '../../components/version-tip/version-tip'
import { StaffTipItemConfig } from '../../constants'

export interface OperateRecord {
    operate_time: string
    operate_type: number
    operate_type_text: string
    source: number
    operator: {
        id: number
        user: IUser
    }
    detail: any
}

export const recordVersionMap: {
    [key: string]: number
} = {
    '-1': 90,
    '0': 7,
    '1': 90,
    '2': 365,
}

const staffRecordTipConfig: StaffTipItemConfig[] = [
    {
        title: '标准版',
        count: 7,
        unit: '天',
    },
    {
        title: '专业版',
        count: 90,
        unit: '天',
    },
    {
        title: '旗舰版',
        count: 1,
        unit: '年',
        limit: 365,
    },
]

const StaffOperateRecordTable = (props: { date: string[] }) => {
    const cls = (...arg: string[]) => getBEMCls('staff-operate-record-table', ...arg)

    const [staffList, setStaffList] = useState<StaffList[]>([])

    const filterRecordTypeData = useMemo(() => {
        return Object.keys(OperateMap).map(i => ({
            text: OperateMap[i],
            value: +i,
        }))
    }, [])

    const filterOperater = useMemo(() => {
        return staffList.map(i => ({
            text: (
                <Row data-align-items-center>
                    <img
                        style={{
                            border: `1px solid ${i.user.gender === 2 ? '#ee5b5b' : '#3c90ff'}`,
                        }}
                        className={cls('avatar')}
                        src={i.user.avatar}
                        alt=""
                    />
                    <div className={cls('nickname')}>{i.user.nickname}</div>
                    <div className={cls('realname')}>({i.user.realname})</div>
                </Row>
            ),
            value: i.id,
        }))
    }, [staffList])

    const columns: ColumnsType<OperateRecord> = [
        {
            title: '时间',
            dataIndex: 'operate_time',
            render: val => dayjs(val).format('MM-DD HH:MM:ss'),
            width: 100,
        },
        {
            title: '操作人',
            key: 'name',
            filters: filterOperater,
            onFilter: (value, record) => record.operator.id === value,
            render: (val, record) => {
                return (
                    <Row data-align-items-center>
                        <img
                            style={{
                                border: `1px solid ${record.operator.user.gender === 2 ? '#ee5b5b' : '#3c90ff'}`,
                            }}
                            className={cls('avatar')}
                            src={record.operator.user.avatar}
                            alt=""
                        />
                        <div className={cls('nickname')}>{record.operator.user.nickname}</div>
                        <div className={cls('realname')}>({record.operator.user.realname})</div>
                    </Row>
                )
            },
        },
        {
            title: '操作模块',
            dataIndex: 'source',
            render: val => OperateMap[`${val}`],
            filters: filterRecordTypeData,
            onFilter: (value, record) => record.source === value,
            width: 150,
        },
        {
            title: '操作内容',
            dataIndex: 'operate_type_text',
            width: 170,
        },
        {
            title: '操作详情',
            render: (val, record) => getOperateDetail(record),
        },
    ]

    useRequest(
        () =>
            $post({
                url: '/staff/getstafflist',
                params: {
                    limit: 20,
                    page: 1,
                    status: 0,
                },
            }),
        {
            onSuccess: res => {
                setStaffList(res.staff_list)
            },
        },
    )

    const requestHandle = useRequest<
        {
            list: OperateRecord[]
            pagination: IPagination
        },
        {
            operate_records: OperateRecord[]
            pagination: IPagination
        }
    >(
        val => {
            return $post({
                url: '/staff/staffoperaterecords',
                params: {
                    limit: 20,
                    page: val?.pagination?.page ? val.pagination.page + 1 : 1,
                    from_date:
                        props.date[0] ||
                        dayjs(dayjs())
                            .subtract(recordVersionMap[`${shopStore.cashierVersion}`], 'd')
                            .format('YYYY-MM-DD'),
                    to_date: props.date[1] || dayjs().format('YYYY-MM-DD'),
                    sources: Object.keys(OperateMap).map(i => +i),
                    operator_ids: staffList.map(s => s.id),
                },
            })
        },
        {
            isNoMore: data => {
                if (data?.pagination) {
                    return data?.pagination.page === data?.pagination.page_count
                } else {
                    return false
                }
            },
            loadMore: true,
            formatResult: data => {
                return {
                    list: data.operate_records,
                    pagination: data.pagination,
                }
            },
            onError: e => {
                $msg('error', e.message)
            },
            refreshDeps: [staffList, props.date],
        },
    )

    return (
        <div className="table-container">
            <AutoResizer>
                {({ height }) => {
                    return (
                        <WithScrollLoad
                            data-expaned
                            data-overflow-hidden
                            scrollloadhandle={requestHandle}
                            bottomtip={
                                shopStore.cashierVersion !== 2 ? (
                                    <div className={cls('tip')}>
                                        <div>
                                            只展示最近{recordVersionMap[`${shopStore.cashierVersion}`]}
                                            天的记录，升级可保留更多记录
                                        </div>
                                        <div
                                            className="action"
                                            data-font-szie-16
                                            data-margin-left-6
                                            onClick={() => {
                                                showLayer(
                                                    <VersionTip
                                                        title={`当前版本只保留近${
                                                            recordVersionMap[`${shopStore.cashierVersion}`]
                                                        }天的记录，是否升级?`}
                                                        config={staffRecordTipConfig}
                                                        count={recordVersionMap[`${shopStore.cashierVersion}`]}
                                                    />,
                                                )
                                            }}
                                        >
                                            了解详情
                                        </div>
                                    </div>
                                ) : undefined
                            }
                        >
                            <Table
                                rowKey={record => record.operate_time}
                                loading={requestHandle.loading || requestHandle.loadingMore || void 0}
                                bordered
                                pagination={false}
                                columns={columns}
                                dataSource={requestHandle.data?.list}
                                scroll={{ x: 'max-content', y: height - (shopStore.cashierVersion !== 2 ? 100 : 50) }}
                            />
                        </WithScrollLoad>
                    )
                }}
            </AutoResizer>
        </div>
    )
}

export default StaffOperateRecordTable
